import React from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  tooltip?: string;
}

interface SegmentToggleProps<T extends string> {
  label?: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

export default function SegmentToggle<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: SegmentToggleProps<T>) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {label ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      ) : null}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        disabled={disabled}
        onChange={(_e, next) => {
          if (next !== null) onChange(next as T);
        }}
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            px: 1.25,
            py: 0.375,
            fontSize: '0.8125rem',
            lineHeight: 1.4,
          },
        }}
      >
        {options.map((opt) => {
          const btn = (
            <ToggleButton key={opt.value} value={opt.value}>
              {opt.label}
            </ToggleButton>
          );
          return opt.tooltip ? (
            <Tooltip key={opt.value} title={opt.tooltip} arrow placement="top">
              {btn}
            </Tooltip>
          ) : (
            btn
          );
        })}
      </ToggleButtonGroup>
    </span>
  );
}
